import { ArrowLeft, ArrowUpRight, CalendarDays } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { NewsAttachments } from '@/components/news';
import {
  MediaFrame,
  NavyAtmosphere,
  ScrollReveal,
  SplitReveal,
} from '@/components/motion';
import { getNewsArticle, relatedNews } from '@/data/news';

/**
 * NewsDetailPage (/news/:slug) — a single article, driven entirely from
 * @/data/news. Unknown slugs redirect to /news (never an empty/fabricated
 * page). A navy text hero carries the category, date and title; the verified
 * lead image follows (cover-cropped for photos, contained for certificates and
 * badges so they are never mis-cropped); then the body, the article's tags, the
 * typed attachments (images · video · documents · links) and related articles.
 *
 * Every fact/figure/quote is reproduced from the source; reduced-motion safe.
 */
export function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getNewsArticle(slug);

  if (!article) {
    return <Navigate to="/news" replace />;
  }

  const related = relatedNews(article);

  return (
    <div className="bg-background">
      {/* ── HERO (navy, text) ────────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden bg-brand-navy pt-28 pb-16 md:pt-32 md:pb-20">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-3xl px-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 font-label text-xs tracking-[0.18em] text-white/70 uppercase transition-colors hover:text-brand-gold-light"
          >
            <ArrowLeft aria-hidden className="size-4" />
            All news
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
              {article.category}
            </span>
            <span aria-hidden className="text-white/30">
              ·
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-white/70">
              <CalendarDays aria-hidden className="size-4" />
              <time dateTime={article.dateISO}>{article.dateDisplay}</time>
            </span>
          </div>

          <SplitReveal
            as="h1"
            by="lines"
            className="mt-4 font-display text-3xl leading-tight text-white md:text-5xl"
          >
            {article.title}
          </SplitReveal>

          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-lg leading-relaxed text-white/75">{article.excerpt}</p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── LEAD IMAGE ───────────────────────────────────────────────────── */}
      <section className="bg-background">
        <div className="mx-auto w-full max-w-4xl px-6">
          <div className="-mt-10 md:-mt-14">
            {article.heroFit === 'cover' ? (
              <MediaFrame
                image={article.heroImage}
                alt={article.title}
                aspect="aspect-[16/9]"
                overlay={0}
                rounded="rounded-2xl"
              />
            ) : (
              <div className="flex items-center justify-center rounded-2xl border border-border bg-muted/40 p-6 shadow-sm md:p-10">
                <img
                  src={article.heroImage}
                  alt={article.title}
                  loading="lazy"
                  className="max-h-[70vh] w-auto max-w-full rounded-lg object-contain"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── BODY ─────────────────────────────────────────────────────────── */}
      <section className="bg-background py-14 md:py-20">
        <div className="mx-auto w-full max-w-3xl px-6">
          <ScrollReveal from="up" stagger={0.05} className="space-y-5">
            {article.body.map((paragraph, index) => (
              <p
                key={index}
                className={
                  index === 0
                    ? 'text-lg leading-relaxed text-foreground md:text-xl'
                    : 'text-base leading-relaxed text-muted-foreground md:text-lg'
                }
              >
                {paragraph}
              </p>
            ))}
          </ScrollReveal>

          {/* Tags */}
          <div className="mt-10 flex flex-wrap gap-2 border-t border-border pt-8">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full border border-border bg-muted/30 px-3 py-1 text-xs font-medium text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── ATTACHMENTS ──────────────────────────────────────────────────── */}
      {article.attachments.length > 0 && (
        <section className="border-t border-border bg-muted/30 py-14 md:py-20">
          <div className="mx-auto w-full max-w-4xl px-6">
            <header className="mb-8 max-w-2xl">
              <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                Attachments &amp; resources
              </p>
              <SplitReveal
                as="h2"
                by="lines"
                className="mt-3 font-display text-2xl leading-tight text-foreground md:text-3xl"
              >
                Explore more from this story.
              </SplitReveal>
            </header>
            <NewsAttachments attachments={article.attachments} />
          </div>
        </section>
      )}

      {/* ── RELATED ──────────────────────────────────────────────────────── */}
      {related.length > 0 && (
        <section className="bg-background py-14 md:py-20">
          <div className="mx-auto w-full max-w-6xl px-6">
            <header className="mb-8 max-w-2xl">
              <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
                Keep reading
              </p>
              <SplitReveal
                as="h2"
                by="lines"
                className="mt-3 font-display text-2xl leading-tight text-foreground md:text-3xl"
              >
                More IBAS news.
              </SplitReveal>
            </header>

            <ScrollReveal
              from="up"
              stagger={0.06}
              as="ul"
              className="grid grid-cols-1 gap-4 sm:grid-cols-3"
            >
              {related.map((other) => (
                <li key={other.slug}>
                  <Link
                    to={`/news/${other.slug}`}
                    className="group flex h-full flex-col justify-between gap-4 rounded-xl border border-border bg-muted/30 p-5 transition-colors duration-300 hover:border-brand-gold/40 hover:bg-muted/60 motion-reduce:transition-none"
                  >
                    <div>
                      <p className="font-label text-xs tracking-[0.16em] text-brand-gold uppercase">
                        {other.category}
                      </p>
                      <p className="mt-2 font-display text-lg leading-snug font-semibold text-foreground">
                        {other.title}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors group-hover:text-brand-gold">
                      Read article
                      <ArrowUpRight
                        aria-hidden
                        className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 motion-reduce:transition-none"
                      />
                    </span>
                  </Link>
                </li>
              ))}
            </ScrollReveal>
          </div>
        </section>
      )}
    </div>
  );
}
